import { useEffect, useState } from "react";
import { Instagram, RefreshCw, Unplug, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function InstagramConnect({ clientId }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = () => api.get(`/clients/${clientId}/instagram/status`).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("ig");
    if (q === "connected") toast.success("Compte Instagram connecté — lancez une première synchronisation");
    if (q === "denied") toast.error("Connexion Instagram annulée");
    if (q === "error") toast.error("Erreur lors de la connexion Instagram");
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/instagram/start`);
      window.location.assign(data.url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/instagram/sync`, { month });
      toast.success(`Stats réelles synchronisées : ${data.media_synced} publication(s), portée ${data.reach}, ${data.followers} abonnés`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter le compte Instagram de ce client ? Les statistiques synchronisées seront supprimées.")) return;
    await api.delete(`/clients/${clientId}/instagram`);
    toast.success("Compte Instagram déconnecté");
    load();
  };

  if (!status) return null;

  return (
    <div className="pt-4 border-t border-neutral-100" data-testid="ig-section">
      <h3 className="font-heading font-bold mb-2 flex items-center gap-2">
        <Instagram className="w-4 h-4" /> Connexion Instagram
      </h3>
      <p className="text-sm text-neutral-500 mb-3">
        Connectez le compte Instagram professionnel du client pour intégrer ses vraies statistiques (abonnés, portée, vues, engagement) dans les rapports mensuels.
      </p>

      {!status.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3.5 text-xs text-amber-800 space-y-1.5" data-testid="ig-not-configured">
          <p className="font-medium">Configuration Meta requise</p>
          <p>Créez une application sur developers.facebook.com (type Business + produit « Instagram — API avec connexion Instagram »), puis renseignez META_IG_APP_ID et META_IG_APP_SECRET dans la configuration serveur.</p>
          <p>URI de redirection à autoriser dans l'app Meta :</p>
          <code className="block bg-white border border-amber-200 rounded px-2 py-1 break-all select-all" data-testid="ig-redirect-uri">{status.redirect_uri}</code>
        </div>
      )}

      {status.configured && !status.connected && (
        <Button data-testid="ig-connect-btn" onClick={connect} className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
          <Instagram className="w-4 h-4" /> Connecter le compte Instagram du client
        </Button>
      )}

      {status.connected && (
        <div className="space-y-3" data-testid="ig-connected">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1.5 font-medium text-green-700">
              <CheckCircle2 className="w-4 h-4" /> @{status.username}
            </span>
            <span className="text-neutral-500">{status.followers_count ?? "—"} abonnés · {status.media_count ?? "—"} publications</span>
            {status.last_sync_at && (
              <span className="text-xs text-neutral-400">Dernière synchro : {new Date(status.last_sync_at).toLocaleString("fr-FR")}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="month" data-testid="ig-sync-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
            <Button data-testid="ig-sync-btn" onClick={sync} disabled={syncing} variant="outline" className="gap-1.5">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Synchronisation…" : "Synchroniser les stats du mois"}
            </Button>
            <Button data-testid="ig-disconnect-btn" onClick={disconnect} variant="outline" size="icon" title="Déconnecter">
              <Unplug className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-neutral-400">Les rapports mensuels intègrent automatiquement les statistiques synchronisées (générez ou régénérez le rapport après synchro).</p>
        </div>
      )}
    </div>
  );
}
